import type { AutomationType } from '@playbook-technologies/common-enums';
import { useState } from 'react';

import { getRole } from '@client/new/components/AdminMenu/helpers';
import type { Async } from '@client/new/helpers/types';

import { AUTOMATION_COPY } from '../automation-copy';
import {
  AutomationStepInsight,
  SendAutomationTestResponse,
} from '../automations.api';
import {
  SendTestInput,
  SendTestStep,
  useSendTestModel,
} from '../model/AutomationSendTest.model';

export type { SendTestStep } from '../model/AutomationSendTest.model';

const MAX_TEST_RECIPIENTS = 5;
export const RECIPIENTS_LIMIT_MESSAGE = `to must contain no more than ${MAX_TEST_RECIPIENTS} elements`;
export const RECIPIENTS_REQUIRED_MESSAGE = 'Add at least one recipient';
export const PUSH_NEEDS_USER_ID_MESSAGE =
  'A push step needs a userId to deliver to';
const USER_ID_FORMAT_MESSAGE = 'userId must be a positive integer';

type StepChannel = 'email' | 'push';

export type SendTestStepOption = {
  value: SendTestStep;
  label: string;
  channel: StepChannel | 'mixed';
};

export type SendTestForm = {
  step: SendTestStep;
  variant: string;
  recipients: string[];
  userId: string;
};

export type SendTestValidation = Partial<
  Record<'recipients' | 'userId', string>
>;

export type SendTestOutcome = { sent: string[]; skipped: string[] };

export type SendTestViewModel = {
  canSendTest: boolean;
  form: SendTestForm;
  stepOptions: SendTestStepOption[];
  variants: readonly string[];
  validation: SendTestValidation;
  canSubmit: boolean;
  sending: boolean;
  outcome: Async<SendTestOutcome, string>;
  setStep: (step: SendTestStep) => void;
  setVariant: (variant: string) => void;
  setRecipients: (recipients: string[]) => void;
  setUserId: (userId: string) => void;
  submit: () => Promise<void>;
};

export const EMPTY_FORM: SendTestForm = {
  step: 'all',
  variant: '',
  recipients: [],
  userId: '',
};

/** Stats steps expose no channel; the server-driven label ("Email 1",
 *  "Push 2") is the only hint until the stats contract carries one. */
const channelOf = (step: AutomationStepInsight): StepChannel =>
  step.label.toLowerCase().startsWith('push') ? 'push' : 'email';

export const toStepOptions = (
  steps: AutomationStepInsight[],
): SendTestStepOption[] => [
  { value: 'all', label: 'All steps', channel: 'mixed' },
  ...steps.map((step, index) => ({
    value: index + 1,
    label: `${index + 1}. ${step.label}`,
    channel: channelOf(step),
  })),
];

const parseUserId = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  return /^[1-9]\d*$/.test(trimmed) ? Number(trimmed) : NaN;
};

export const validateSendTest = (
  form: SendTestForm,
  stepOptions: SendTestStepOption[],
): SendTestValidation => {
  const validation: SendTestValidation = {};
  if (form.recipients.length === 0) {
    validation.recipients = RECIPIENTS_REQUIRED_MESSAGE;
  } else if (form.recipients.length > MAX_TEST_RECIPIENTS) {
    validation.recipients = RECIPIENTS_LIMIT_MESSAGE;
  }

  const userId = parseUserId(form.userId);
  const picked = stepOptions.find((option) => option.value === form.step);
  if (userId !== undefined && Number.isNaN(userId)) {
    validation.userId = USER_ID_FORMAT_MESSAGE;
  } else if (picked?.channel === 'push' && userId === undefined) {
    validation.userId = PUSH_NEEDS_USER_ID_MESSAGE;
  }
  return validation;
};

export const toSendTestInput = (form: SendTestForm): SendTestInput => {
  const userId = parseUserId(form.userId);
  return {
    step: form.step,
    variant: form.variant,
    recipients: form.recipients,
    ...(userId !== undefined && !Number.isNaN(userId) ? { userId } : {}),
  };
};

const toRecipients = (to: string[] | number): string =>
  Array.isArray(to) ? to.join(', ') : `user ${to}`;

export const toOutcome = (
  response: SendAutomationTestResponse,
): SendTestOutcome => ({
  sent: response.sent.map(
    (entry) =>
      `Step ${entry.step} · ${entry.channel} · ${entry.templateKey} · ${
        entry.variant ?? 'default'
      } → ${toRecipients(entry.to)}`,
  ),
  skipped: response.skipped.map(
    (entry) => `Step ${entry.step} · ${entry.channel} skipped: ${entry.reason}`,
  ),
});

export const useSendTestViewModel = (
  creatorId: number,
  automationType: AutomationType,
): Async<SendTestViewModel, string> => {
  const model = useSendTestModel(creatorId, automationType);
  const [form, setForm] = useState<SendTestForm>(EMPTY_FORM);

  if (model.type === 'fetch-error') {
    return { type: 'fetch-error', value: "We couldn't load automation steps." };
  }
  if (model.type !== 'fetched') return model;

  const stepOptions = toStepOptions(model.value.steps);
  const validation = validateSendTest(form, stepOptions);
  const sending = model.value.result.type === 'fetching';
  const canSubmit = Object.keys(validation).length === 0 && !sending;
  const result = model.value.result;
  const outcome: Async<SendTestOutcome, string> =
    result.type === 'fetched'
      ? { type: 'fetched', value: toOutcome(result.value) }
      : result;

  const patch = (delta: Partial<SendTestForm>) =>
    setForm((current) => ({ ...current, ...delta }));

  return {
    type: 'fetched',
    value: {
      canSendTest: getRole() === 'admin',
      form,
      stepOptions,
      variants: AUTOMATION_COPY[automationType].variants,
      validation,
      canSubmit,
      sending,
      outcome,
      setStep: (step) => patch({ step }),
      setVariant: (variant) => patch({ variant }),
      setRecipients: (recipients) => patch({ recipients }),
      setUserId: (userId) => patch({ userId }),
      submit: async () => {
        if (!canSubmit) return;
        await model.value.send(toSendTestInput(form));
      },
    },
  };
};
